import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { trackEvent, trackInvalidLink } from '../lib/analytics';
import PostHogFeatures from '../components/PostHogFeatures';
import { useIsClient } from '../lib/hooks/useHydration';

export default function InvalidLinkPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const [platform, setPlatform] = useState<'ios' | 'android' | 'web'>('web');
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) return;
    
    // Detect mobile platform
    const userAgent = window.navigator.userAgent;
    if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      setPlatform('ios');
    }

    // Track invalid link access for analytics
    const fromPath = router.query.from as string || 'unknown';
    trackInvalidLink(window.location.href, 'page_redirect', {
      referrer_path: fromPath,
      user_agent: userAgent,
    });
    
    // Also track with legacy analytics
    trackEvent('invalid_link_accessed', {
      referrer_path: fromPath,
      timestamp: new Date().toISOString(),
      user_agent: userAgent,
    });
  }, [isClient, router.query.from]);

  const handleDownloadApp = () => {
    trackEvent('app_download_click', {
      source: 'invalid_link_page',
      platform: platform,
    });
    window.open('https://in.invitd.us/7CQHZYckiZb', '_blank');
  };

  const getAppStoreLink = () => {
    if (platform === 'android') {
      return 'https://play.google.com/store/apps/details?id=com.invitd.app';
    } else if (platform === 'ios') {
      return 'https://apps.apple.com/app/invitd/id1548409309';
    }
    return 'https://in.invitd.us/7CQHZYckiZb';
  };

  const features = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
      title: 'Create in Seconds'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Track RSVPs Live'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
      ),
      title: 'Share Instantly'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      title: 'Auto Reminders'
    }
  ];

  return (
    <>
      <Head>
        <title>Invalid Invite Link - Invitd</title>
        <meta name="description" content="The invite link you're looking for is invalid or has expired. Download Invitd to never miss an event again!" />
        <meta name="robots" content="noindex, nofollow" />
        <style dangerouslySetInnerHTML={{ __html: `
          @media (max-height: 750px) {
            .responsive-h1 { font-size: 1.75rem !important; }
            .responsive-h2 { font-size: 1.125rem !important; }
            .responsive-text { font-size: 0.875rem !important; }
            .responsive-logo { height: 3rem !important; width: 3rem !important; }
            .responsive-padding { padding: 1.25rem !important; }
            .responsive-mb { margin-bottom: 0.75rem !important; }
            .responsive-mt { margin-top: 0.75rem !important; }
            .responsive-button-py { padding-top: 0.625rem !important; padding-bottom: 0.625rem !important; }
            .responsive-gap { gap: 0.5rem !important; }
          }
          @keyframes blob {
            0% { transform: translate(0px, 0px) scale(1); }
            33% { transform: translate(30px, -50px) scale(1.1); }
            66% { transform: translate(-20px, 20px) scale(0.9); }
            100% { transform: translate(0px, 0px) scale(1); }
          }
          .animate-blob {
            animation: blob 7s infinite;
          }
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          .animation-delay-4000 {
            animation-delay: 4s;
          }
        `}} />
      </Head>

      <PostHogFeatures userRole="guest" />

      <main className="min-h-screen relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-sky-400 via-sky-500 to-blue-500">
          <div className="absolute inset-0 opacity-50">
            <div className="absolute top-10 left-4 w-32 h-32 sm:w-48 sm:h-48 md:w-72 md:h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-20 right-4 w-28 h-28 sm:w-44 sm:h-44 md:w-72 md:h-72 bg-sky-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-20 left-10 w-36 h-36 sm:w-52 sm:h-52 md:w-72 md:h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
        </div>
        
        {/* Content */}
        <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-6">
          <div className="max-w-md w-full space-y-4">
            
            {/* Error Card - Compact */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-5 responsive-padding text-center border border-white/20">
              {/* Logo */}
              <div className="mx-auto h-16 w-16 responsive-logo rounded-full bg-white shadow-lg border-4 border-gray-100 mb-4 responsive-mb overflow-hidden">
                <img 
                  src="/icon.png" 
                  alt="Invitd Logo" 
                  className="h-full w-full object-cover"
                />
              </div>

              {/* Error Message */}
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-3 responsive-mb">
                <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-amber-800">Link Invalid or Expired</span>
              </div>

              <h1 className="text-2xl responsive-h1 font-bold text-gray-900 mb-2 responsive-mb">
                Oops! This link is invalid 🤔
              </h1>
              
              <p className="text-sm responsive-text text-gray-600 mb-4 responsive-mb">
                Ask your host to resend the invite, or check for typos in the URL.
              </p>

              <button
                onClick={() => window.location.reload()}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2"
              >
                Try Again
              </button>
            </div>

            {/* App Promotion Card */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-5 responsive-padding border border-white/20">
              <div className="text-center mb-4 responsive-mb">
                <h2 className="text-xl responsive-h2 font-bold text-gray-900 mb-1">
                  Ditch the Party Group Texts 👋
                </h2>
                <p className="text-sm responsive-text text-gray-600">
                  Plan your next event in under a minute
                </p>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-2 responsive-gap mb-4 responsive-mb">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 hover:bg-sky-50 transition-colors">
                    <div className="flex-shrink-0 w-7 h-7 rounded-md bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-white">
                      {feature.icon}
                    </div>
                    <p className="text-xs font-semibold text-gray-900">{feature.title}</p>
                  </div>
                ))}
              </div>

              {/* Download Button */}
              <button
                onClick={handleDownloadApp}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3.5 responsive-button-py px-6 rounded-xl font-bold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 shadow-[0_4px_20px_rgba(124,58,237,0.35)] flex items-center justify-center gap-2 text-base responsive-text"
              >
                {/* Only render platform-specific icons after hydration to avoid mismatch */}
                {isClient && platform === 'ios' && (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                )}
                {isClient && platform === 'android' && (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.523 15.341c-.583 0-1.057.474-1.057 1.057 0 .583.474 1.057 1.057 1.057s1.057-.474 1.057-1.057c0-.583-.474-1.057-1.057-1.057zm-11.046 0c-.583 0-1.057.474-1.057 1.057 0 .583.474 1.057 1.057 1.057s1.057-.474 1.057-1.057c0-.583-.474-1.057-1.057-1.057zm11.405-6.02l1.989-3.445a.413.413 0 00-.715-.413l-2.014 3.489A12.18 12.18 0 0012 8.19c-1.843 0-3.581.271-5.142.762L4.844 5.463a.413.413 0 00-.715.413l1.989 3.445C2.927 11.143.394 14.649 0 18.922h24c-.394-4.273-2.927-7.779-6.118-9.601z"/>
                  </svg>
                )}
                {(!isClient || platform === 'web') && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
                Download Now
              </button>

              {/* Trust Indicator */}
              <div className="flex items-center justify-center gap-2 mt-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-sm font-bold text-gray-800">4.8</span>
                <span className="text-sm text-gray-400">•</span>
                <span className="text-sm text-gray-600">1M+ events hosted</span>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center">
              <a 
                href="https://invitd.us" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-white/60 hover:text-white/90 transition-colors"
              >
                Learn more at invitd.us
              </a>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
